import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { colors2, LoaderCircleV2 } from "oolib";
import { UI_BODY, UI_BODY_SM, UI_BODY_SEMIBOLD, UI_CAPTION, UI_TAG_DF, UI_TITLE } from "oolib/dist/v2/components/Typo2";
import { useGetTopicClusteringTreeCounts, useGetPriorityFindings } from "@/utils/react-query-hooks/aiAnnotation";
import { grainNoun } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/format";
import { usePeriodSelection } from "@/components/analysisModules/TopicClusteringTreeViewV2/periodSelection";
import { PeriodSelector } from "@/components/analysisModules/TopicClusteringTreeViewV2/PeriodPicker";
import { resolveZones } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/zoneStyles";
import { SM_UIContent } from "@/_self_managed/SM_UIContent";
import { FindingEvidencePanel } from "./FindingEvidencePanel";

/**
 * Findings — the read-out a PM opens first. One flat list, no filters, one
 * request: the server scores the clustering tree over the last periods,
 * runs the rules that detect what changed / stands out / disagrees (okf-be
 * priorities/findings), ranks, and attaches the model's read-out where it
 * validated (POST getPriorityFindings). A card whose read-out did not
 * validate, or when the read-out failed altogether, shows the computed
 * wording instead.
 */

// The dashboard pads this tab at the sides only; the top matches the
// Overview tab's own padding.
const Page = styled.div`
  max-width: 880px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 0 40px;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  padding: 16px 18px;
  border-radius: 12px;
  background: ${colors2.white};
  border: 1px solid ${(p) => (p.$selected ? colors2.grey30 : colors2.grey10)};
  cursor: pointer;
  transition: border-color 120ms ease, box-shadow 120ms ease;
  &:hover {
    border-color: ${colors2.grey30};
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
  }
`;

const MAX_FINDINGS = 12;

// The zone whose text colour a kind's label borrows — fires-red for bad
// news, resolving-green for good, emerging-amber for "look closer", grey
// for the rest — set the way the zone cards set their own label.
const KIND_ZONE = {
  caughtFire: "fires",
  dominantSubtopic: "emerging",
  topicOnFire: "fires",
  escalation: "emerging",
  turnedAround: "resolving",
  channelContrast: "emerging",
  resolving: "resolving",
  winSlipping: "fires",
  zoneMove: "background",
  goneSilent: "background",
  loudVoice: "background",
  quietConsensus: "background",
  brightSpot: "resolving",
  praiseVsPain: "emerging",
  painNoPraise: "fires",
  oppositeDirections: "emerging",
};


export const FindingsTab = () => {
  // The period, and what it is compared with: the selection shared with the
  // Overview, Topics and Priorities (one provider above the routes).
  const periodSelection = usePeriodSelection();
  const { period, compareKey } = periodSelection;
  // The tenant's words (zone names, kind names), resolved on the server —
  // the same counts request the Overview and Priorities already hold.
  const { data: counts } = useGetTopicClusteringTreeCounts({ withPriorityStats: true });
  const zoneStyles = useMemo(() => resolveZones(counts?.prioritiesCopy), [counts]);

  const { data, isLoading, error } = useGetPriorityFindings({ period, compareKey, limit: MAX_FINDINGS });
  const findings = data?.findings || [];
  const meta = data?.meta || null;
  // The card whose evidence is open, by id — the panel re-derives on the
  // server for the same period and comparison.
  const [openId, setOpenId] = useState(null);
  const openFinding = findings.find((i) => i.id === openId) || null;

  if (isLoading) return <LoaderCircleV2 />;
  if (error) return <UI_BODY_SM style={{ color: colors2.grey60 }}>{SM_UIContent.SM_Dashboard.findingsError}</UI_BODY_SM>;
  if (data && !data.tree) {
    return (
      <UI_BODY style={{ color: colors2.grey60 }}>
        {SM_UIContent.SM_Dashboard.findingsEmpty}
      </UI_BODY>
    );
  }
  if (!period || !meta) {
    return <UI_BODY_SM style={{ color: colors2.grey60 }}>No dated observations to read findings from yet.</UI_BODY_SM>;
  }
  // In history mode the comparison period plays no part (as on Priorities).
  const compareLockedNote = meta.mode === "history" ? SM_UIContent.SM_Dashboard.compareLocked(meta.historyPeriods, grainNoun(meta.timeGrain), "findings") : null;
  // A thin period, or a yearly view with one year of data: the rules go
  // quiet, and the page says why rather than jumping to another period.
  // What the rules actually read: the lookback's observations, the selected
  // period included — the per-period totals the scorer sends cover exactly it.
  const lookbackTotal = meta.lookbackPeriods && meta.totalsByKey ? Object.values(meta.totalsByKey).reduce((a, n) => a + (n || 0), 0) : null;
  const thinNote = meta.totalNow < meta.minNowTotal ? SM_UIContent.SM_Dashboard.findingsThinPeriod(meta.totalNow, meta.nowLabel, meta.minNowTotal) : null;
  const quietYearNote = !thinNote && meta.timeGrain === "year" && !findings.length ? SM_UIContent.SM_Dashboard.findingsQuietYear : null;

  const status = data?.narrationError ? "Read-out unavailable — showing the computed wording." : null;

  return (
    <Page>
      <PeriodSelector id="findings" inline selection={periodSelection} compareLockedNote={compareLockedNote} />
      <Header>
        <div>
          <UI_BODY_SEMIBOLD style={{ color: colors2.grey80 }}>Findings · {meta.nowLabel}</UI_BODY_SEMIBOLD>
          <UI_CAPTION style={{ color: colors2.grey60, display: "block" }}>
            {data.total} found · {meta.totalNow} observation{meta.totalNow === 1 ? "" : "s"} in {meta.nowLabel}
            {lookbackTotal !== null ? `, ${lookbackTotal} across the last ${meta.lookbackPeriods} ${grainNoun(meta.timeGrain)}` : ""}
            {meta.basisText ? ` · ${meta.basisText}` : ""}
            {data.total > MAX_FINDINGS ? ` · showing the top ${MAX_FINDINGS}` : ""}
          </UI_CAPTION>
        </div>
        {status && <UI_CAPTION style={{ color: colors2.grey50 }}>{status}</UI_CAPTION>}
      </Header>

      {(thinNote || quietYearNote) && <UI_BODY_SM style={{ color: colors2.grey60 }}>{thinNote || quietYearNote}</UI_BODY_SM>}
      {!findings.length && !thinNote && !quietYearNote && (
        <UI_BODY_SM style={{ color: colors2.grey60 }}>
          Nothing stands out in {meta.nowLabel} yet — no zone changes, contrasts or concentrations cleared the evidence floor.
        </UI_BODY_SM>
      )}

      {findings.map((finding) => (
          <Card
            key={finding.id}
            $selected={finding.id === openId}
            role="button"
            tabIndex={0}
            onClick={() => setOpenId((cur) => (cur === finding.id ? null : finding.id))}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpenId((cur) => (cur === finding.id ? null : finding.id));
              }
            }}
          >
            <UI_TAG_DF style={{ color: zoneStyles[KIND_ZONE[finding.kind] || "background"].text }}>{finding.kindLabel}</UI_TAG_DF>
            <UI_TITLE style={{ color: colors2.grey80 }}>{finding.headline}</UI_TITLE>
            <UI_BODY_SM style={{ color: colors2.grey60 }}>{finding.why}</UI_BODY_SM>
            <UI_CAPTION style={{ color: colors2.grey40 }}>Click to see the evidence</UI_CAPTION>
          </Card>
      ))}

      <FindingEvidencePanel finding={openFinding} period={period} compareKey={compareKey} zoneStyles={zoneStyles} onClose={() => setOpenId(null)} />
    </Page>
  );
};
